<template>
  <row>
    <column :size="fullWidth ? '100%' : '50%'">
      <row>
        <column>
          <label :for="`update_field_${propertyUpdate.client_id + '_' +propertyUpdate.property_client_id}`" class="a-select-label">
            {{propertyLabel}}
          </label>
        </column>
        <suffix v-if="deleteable && fullWidth">
          <button @click.stop="open" ref="button" class="dropdown-toggle btn-icon clear-area controllers kebab"
                  :id="`propertyUpdateDropdown${propertyUpdate.client_id}`" type="button">
            <icon class="kebab"></icon>
            <span class="sr-only">Property Update Dropdown</span>
          </button>
          <context-menu ref="contextMenu"
                        :state="modalStateFor(vueModalID.sortableContextMenu)"
                        :index="vueModalID.sortableContextMenu"
                        fixed
                        @close="toggleModal">
            <ul>
              <li>
                <button @click.stop="deleteItem" class="dropdown-item">Delete</button>
              </li>
            </ul>
          </context-menu>
        </suffix>
      </row>
      <config-field-validation :model="propertyUpdate.parent.parent" :field-name="`clearWhenRequired-${propertyUpdate.parent.client_id}`" message="This property is required">
        <a-select
          v-model="propertyUpdate.property_client_id"
          :options="updateablePropertiesVSelectOptions"
          compress
          @input="onPropertySelected"
          :id="`update_field_${propertyUpdate.client_id + '_' +propertyUpdate.property_client_id}`"
          placeholder="Select a Property..."
          ref="propertySelect"
        ></a-select>
      </config-field-validation>
    </column>

    <column v-if="selectedPropertyIsMultiPicklist" size="100%">
      <row>
        <column :size="fullWidth ? '100%' : '50%'">
          <a-select
            :options="multiPicklistModeOptions"
            v-model="propertyUpdate.multi_picklist_mode"
            placeholder="Select action type..."
            compress
          ></a-select>
        </column>
      </row>
    </column>

    <column v-if="!selectedPropertyIsMultiPicklist" :size="fullWidth ? '100%' : '50%'">
      <div class="radio-inline">
        <input type="checkbox" v-model="propertyUpdate.clear_value" :id="`clear_value_${propertyUpdate.client_id}`" />
        <label :for="`clear_value_${propertyUpdate.client_id}`" >Erase property value</label>
      </div>
    </column>

    <column :size="fullWidth ? '100%' : '50%'" v-if="showNewValueInput">
      <row>
        <column>
          <label :for="`update_value_${propertyUpdate.property_client_id}`" class="a-select-label">
            {{effectiveValueLabel}}
          </label>
        </column>
        <suffix v-if="deleteable && !fullWidth">
          <button @click.stop="open" ref="button" class="dropdown-toggle btn-icon clear-area controllers kebab"
                  :id="`propertyUpdateDropdown${propertyUpdate.client_id}`" type="button">
            <icon class="kebab"></icon>
            <span class="sr-only">Property Update Dropdown</span>
          </button>

          <context-menu ref="contextMenu"
                        :state="modalStateFor(vueModalID.sortableContextMenu)"
                        :index="vueModalID.sortableContextMenu"
                        fixed
                        @close="toggleModal">
            <ul>
              <li>
                <button @click.stop="deleteItem" class="dropdown-item">Delete</button>
              </li>
            </ul>
          </context-menu>
        </suffix>
      </row>
      <!--    Slot for input for value to update to-->
      <slot>
        <input :disabled="propertyUpdate.clear_value" id="value_to_update_to" v-model="propertyUpdate.value_to_update_to" type="text">
      </slot>
    </column>
  </row>
</template>


<script lang="ts">
import Vue from "vue";
import {IDefaultVOption, IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import PropertyUpdate from "~/models/propertyUpdate";
import ContextMenu from "~/components/contextMenu.vue"
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import FormulaWrapper from "~/components/formulas/formulaWrapper.vue";
import ConfigFieldValidation from "~/components/modelProperties/config-field-validation.vue";
import {ObjectModelPropertyRootType} from "~/enums/objectModelPropertyType";
import {PickListSelectionType} from "~/enums/pickListSelectionType";
import {MultiPicklistUpdateMode} from "~/enums/multiPicklistUpdateMode";

export default Vue.extend({
    components: {ContextMenu, FormulaWrapper, ConfigFieldValidation},
    props: {
      propertyUpdate: {
        type: Object as () => PropertyUpdate,
        required: true
      },
      valueLabel:{
        type: String,
        default: "Update to..."
      },
      propertyLabel:{
        type: String,
        default: "Update Which Property?"
      },
      propertyOptions: {
        type: Array,
        required: true
      },
      fullWidth:{
        type: Boolean,
        default: true
      },
      deleteable:{
        type: Boolean,
        default: true
      }
    },
    computed: {
      updateablePropertiesVSelectOptions: {
        get(): IVOptionSetLocal {
          return {
            options: this.propertyOptions,
            valueKey: 'client_id',
            labelKey: 'label_title',
            selectedOption: this.selectedProperty,
          }
        }
      },
      selectedProperty(): ObjectModelProperty | null {
        return ObjectModelProperty.repo.find(this.propertyUpdate.property_client_id);
      },
      selectedPropertyIsMultiPicklist(): boolean {
        return this.selectedProperty?.property_type === ObjectModelPropertyRootType.PickList && this.selectedProperty.selection_type === PickListSelectionType.MultiSelect;
      },
      multiPicklistModeOptions(): IDefaultVOption[] {
        return [
          {label: 'Add to existing list', value: MultiPicklistUpdateMode.Add},
          {label: 'Replace existing list', value: MultiPicklistUpdateMode.Replace},
          {label: 'Remove from existing list', value: MultiPicklistUpdateMode.Remove},
          {label: 'Clear all values', value: MultiPicklistUpdateMode.Clear},
        ];
      },
      effectiveValueLabel(): string {
        if (!this.selectedPropertyIsMultiPicklist) {
          return this.valueLabel;
        }
        switch (this.propertyUpdate.multi_picklist_mode) {
          case MultiPicklistUpdateMode.Add:
            return 'Select values to add...';
          case MultiPicklistUpdateMode.Replace:
            return 'Select values to replace with...';
          case MultiPicklistUpdateMode.Remove:
            return 'Select values to remove...';
          case MultiPicklistUpdateMode.Clear:
            return '';
        }
      },
      showNewValueInput(): boolean {
        if (!this.propertyUpdate.property_client_id) {
          return false;
        }
        if (this.selectedPropertyIsMultiPicklist && this.propertyUpdate.multi_picklist_mode === MultiPicklistUpdateMode.Clear) {
          return false;
        }
        return true;
      },
    },
    mounted() {
      if (this.selectedPropertyIsMultiPicklist) {
        this.propertyUpdate.is_multi_picklist = true;
        // convert to multi picklist update mode
        if (this.propertyUpdate.clear_value) {
          this.propertyUpdate.clear_value = false;
          this.propertyUpdate.multi_picklist_mode = MultiPicklistUpdateMode.Clear;
        }
      } else {
        // if the property we refer to used to be multi-select and no longer is, reset the property update
        if (this.propertyUpdate.is_multi_picklist) {
          this.propertyUpdate.is_multi_picklist = false;
          this.propertyUpdate.property_client_id = null;
          this.propertyUpdate.value_to_update_to_array = [];
          this.propertyUpdate.multi_picklist_mode = MultiPicklistUpdateMode.Replace;
          // unset the last selected option, otherwise the a-select button may still have the label of the previously selected property
          Vue.nextTick(() => {
            this.$refs.propertySelect.vOptionSetManager.setLastSelected(null);
          });
        }
      }
    },
    methods: {
      onPropertySelected(): void{
        this.propertyUpdate.value_to_update_to = null;
        this.propertyUpdate.values_to_update_to_array = [];

        this.propertyUpdate.is_multi_picklist = this.selectedPropertyIsMultiPicklist;
      },
      open(event): void {
        this.showModal(this.vueModalID.sortableContextMenu);
        this.$refs.contextMenu.open(event);
      },
      close(): void {
        this.$refs.contextMenu.close();
      },
      deleteItem(): void {
        this.$emit('remove');
        this.close()
      },
    },
  });
</script>